<template>
  <div class="w-100">
    <h3 class="m-4 text-nowrap font-size-2xl d-none d-lg-block">{{ $t('shoppingLists') }}</h3>
    <div v-if="loading || isLoading" class="list-spinner-wrap d-flex">
      <WCSpinner class="mx-auto align-self-center" size="xl" />
    </div>
    <!-- START : Shopping List -->
    <div v-else class="list-details mx-lg-4">
      <!-- START : Shopping List Header -->
      <div class="list-details__header d-lg-flex justify-content-between align-items-center">
        <!-- START : Shopping List Title -->
        <div class="list-details__header__title h3 mb-0 text-primary font-weight-bold">
          <WCShoppingListIcon
            v-if="list && list.type"
            class="text-primary mr-2"
            :shoppingListType="list.type"
          />
          {{ list && list.name }}
        </div>
        <!-- END : Shopping List Title -->
        <!-- START : Shopping List Button Section -->
        <div class="list-details__header__btn-section">
          <button
            class="wc-add-item__button btn btn-link link-primary underline-link-from-center text-body text-nowrap font-weight-bold h4 mr-0"
            @click="addSelectedToCart(checkedListItems)"
            v-if="!isCustomerModeScan && $configuration.cartEnabled"
          >
            <font-awesome-icon class="mr-2" icon="cart-shopping" />{{ $t('addSelectedToCart') }}
          </button>

          <button
            class="wc-remove-item__button btn btn-link link-primary underline-link-from-center text-body text-nowrap font-weight-bold h4 mr-0"
            id="wc-remove-selected-item"
            @click="removeSelectedItems"
          >
            <font-awesome-icon class="mr-2" icon="trash" />{{ $t('removeSelected') }}
          </button>
        </div>
        <!-- END : Shopping List Button Section -->
      </div>
      <!-- END : Shopping List Header -->

      <div class="mx-4 mx-lg-0">
        <!-- START : Select All Products -->
        <div
          class="px-lg-2 py-3 d-flex justify-content-between"
          v-if="list && list.items && list.items.length"
        >
          <WCCheckbox
            v-model="isAllSelected"
            class="text-primary font-weight-bold"
            :label="'Select All Items'"
          />
          <div class="text-right">
            <div class="font-size-sm">
              {{ list && list.items.length | items }}
              <div class="text-dark" v-if="checkedListItems.length">
                {{ checkedListItems.length | items }} {{ $t('selected') }}
              </div>
            </div>
          </div>
        </div>
        <!-- END : Select All Products -->

        <!-- START : Shopping List Products -->
        <div v-if="!loading && list">
          <div
            class="shopping-list-item-border pb-3 my-3 row justify-content-between"
            v-for="(listItem, i) in sortedList"
            :key="i"
          >
            <!-- START : Shopping List Checkbox -->
            <div
              class="col-1 d-flex align-items-center justify-content-flex-start justify-content-lg-center"
            >
              <WCCheckbox
                :value="listItem"
                v-model="checkedListItems"
                :ariaLabel="listItem && listItem.item && listItem.item.name"
              />
            </div>
            <!-- END : Shopping List Checkbox -->

            <WCProductDetailsImage :item="listItem.item" class="col-3 col-md-2 pl-3 pl-lg-4" />

            <WCListProductDetails
              :listViewScanMode="isCustomerModeScan"
              :listItem="listItem"
              @remove="removeItem($event)"
            />
          </div>
        </div>
        <!-- END : Shopping List Products -->
      </div>
    </div>
    <!-- END : Shopping List -->
  </div>
</template>

<script>
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import WCProductDetailsImage from '@/modules/lists/components/WCProductDetailsImage/WCProductDetailsImage.vue';
import WCListProductDetails from '@/modules/lists/components/WCListProductDetails/WCListProductDetails.vue';
import WCShoppingListIcon from '@/modules/lists/components/WCShoppingListIcon/WCShoppingListIcon.vue';
import ItemCountMixin from '@/modules/lists/mixins/ItemCountMixin';
import ItemAddSelectedToCartMixin from '@/modules/lists/mixins/ItemAddSelectedToCartMixin';

export default {
  name: 'List',
  components: {
    WCSpinner,
    WCCheckbox,
    WCProductDetailsImage,
    WCListProductDetails,
    WCShoppingListIcon,
  },

  // Mixin for add selected to cart and add modifier item to cart
  mixins: [ItemCountMixin, ItemAddSelectedToCartMixin],

  async created() {
    this.getList();
  },
  data() {
    return {
      list: {},
      checkedListItems: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      isGiftCard: 'cart/isGiftCard',
      isLoading: 'lists/isLoading',
      getItemVariation: 'items/getItemVariation',
      isCustomerModeScan: 'user/isCustomerModeScan',
    }),
    sortedList() {
      function compare(a, b) {
        if (a.item.shelfSequence < b.item.shelfSequence) return -1;
        if (a.item.shelfSequence > b.item.shelfSequence) return 1;
        return 0;
      }
      return this.list.items.slice(0).sort(compare);
    },
    isAllSelected: {
      get() {
        return this.list?.items.every(listItem => this.checkedListItems.includes(listItem));
      },
      set(value) {
        if (value) {
          this.checkedListItems = this.list?.items.map(listItem => listItem);
        } else {
          this.checkedListItems = [];
        }
      },
    },
  },

  methods: {
    ...mapActions({
      removeListItems: 'lists/removeListItems',
      removeListItem: 'lists/removeListItem',
      setItemQuantity: 'cart/setItemQuantity',
    }),
    async getList() {
      try {
        this.loading = true;
        const response = await axios.get(`api/me/lists/${this.$route.params.id}`);
        this.list = response.data;
        this.loading = false;
        if (!response.data || response.data.items?.length <= 0) {
          this.$router.push({ name: 'Lists' });
        }
      } catch (error) {
        this.loading = false;
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message,
          },
          timeout: 7000,
        });
      }
    },
    /**
     * Method to remove individual items using trash icon
     * @param {Object} payload - { item, variation }
     */
    async removeItem({ item, variation }) {
      try {
        await this.removeListItem({
          list: this.list,
          item,
          variation,
        });
        this.getList();
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message,
          },
          timeout: 7000,
        });
      }
    },
    /**
     * Method to remove all selected item with delete selected option
     */
    async removeSelectedItems() {
      try {
        await this.removeListItems({
          list: this.list,
          items: this.checkedListItems,
        });
        this.getList();
        this.checkedListItems = [];
      } catch (error) {
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message: error.message,
          },
          timeout: 7000,
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

.wc-list__image-link {
  display: flex;
  width: rem(100px);
  @include media-breakpoint-down(sm) {
    width: rem(60px);
  }
}
.wc-header {
  background-color: rgba(var(--secondary-rgb), 0.1);
}

.list-spinner-wrap {
  height: rem(500px);
}

@include media-breakpoint-down(xs) {
  .shopping-list-item-border {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &:last-child {
      border: 0;
    }
  }
}

.list-details {
  &__header {
    &__title {
      @include media-breakpoint-down(md) {
        padding: $pad-3 * 0.75 $pad-4;
        border-top: 3px solid var(--secondary-lighter-3, get-color('secondary', 'lighter-3'));
        text-align: left;
        background-color: var(--secondary-lighter-7, get-color('secondary', 'lighter-7'));
      }
    }
    &__btn-section {
      @include media-breakpoint-down(md) {
        display: flex;
        justify-content: flex-end;
        padding: $pad-3 * 0.75 $pad-3;
      }
      @include media-breakpoint-only(xs) {
        flex-direction: column;
        align-items: flex-end;
      }
    }
  }
}
</style>
